import { jsx } from '@emotion/react/jsx-runtime';
import { useTheme } from '@emotion/react';
import { compound, createPolymorphicComponent, useSlot, AdaptiveHeightProvider, SlotProvider } from '@nettbureau/core';
import { useCss } from '@nettbureau/styling';
import { isThemeToken } from '@nettbureau/theme';
import { Children, isValidElement } from 'react';
import { Divider } from '../divider/divider.js';
import { Item } from './item.js';

/**
 * Stack enables stacking children along the vertical or horizontal axis using flex-box.
 */ const Stack = compound('Stack', createPolymorphicComponent((props, ref)=>{
    const [mergedProps, mergedRef] = useSlot('Stack', props, ref);
    const { align = props.direction === 'vertical' ? 'stretch' : 'start', as: Component = 'div', children, css, direction = 'horizontal', gap, height, inline, justify = 'start', margin, maxHeight, maxWidth, minHeight, minWidth, padding, reversed = false, separator = false, width, wrap = false, typography, ...otherProps } = mergedProps;
    const theme = useTheme();
    const effectiveHeight = height ?? minHeight;
    const heightToken = isThemeToken(effectiveHeight, theme.heights) ? effectiveHeight : undefined;
    const stackCss = useCss(({ mixins })=>[
            mixins.flex({
                align,
                direction,
                gap,
                inline,
                justify,
                reversed,
                wrap
            }),
            mixins.size({
                height,
                maxHeight,
                maxWidth,
                minHeight,
                minWidth,
                width
            }),
            mixins.spacing({
                padding,
                margin
            }),
            typography && mixins.typography(typography)
        ]);
    const items = Children.toArray(children).map((child, index)=>typeof child === 'string' ? /*#__PURE__*/ jsx(Item, {
            children: child
        }, index) : child).filter(isValidElement);
    const itemsWithSeparators = items.flatMap((item, index)=>[
            item,
            /*#__PURE__*/ isValidElement(separator) ? /*#__PURE__*/ jsx(Item, {
                children: separator
            }, index) : /*#__PURE__*/ jsx(Divider, {}, index)
        ]).slice(0, -1);
    const overrides = {
        Divider: {
            direction: direction === 'vertical' ? 'horizontal' : 'vertical'
        }
    };
    return /*#__PURE__*/ jsx(Component, {
        "data-stack": true,
        ...otherProps,
        ref: mergedRef,
        css: [
            stackCss,
            css
        ],
        children: /*#__PURE__*/ jsx(AdaptiveHeightProvider, {
            value: heightToken ?? null,
            children: /*#__PURE__*/ jsx(SlotProvider, {
                value: overrides,
                children: separator ? itemsWithSeparators : items
            })
        })
    });
}), {
    Item
});

export { Stack };
//# sourceMappingURL=stack.js.map
